import { useEffect, useMemo, useState } from 'react'
import { useStocktakeData } from '../hooks/useStocktakeData.js'
import { formatMoney, formatQty } from '../lib/format.js'
import { Skeleton, SkeletonCard, SkeletonList } from './Skeleton.jsx'
import Toast from './Toast.jsx'

// Stocktake — physical-count workflow.
//
// Two modes:
//   1. Idle (no open stocktake): show "Start new count" + history
//   2. Active (open stocktake): per-item count input with live variance
//
// Saving the count for an individual item is a partial save —
// "Save & complete" calls the complete_stocktake RPC which atomically
// snapshots the counts to on_hand_snapshots, updates items.current_on_hand,
// and marks the stocktake completed. From then on, Variance reporting
// uses the most-recent stocktake snapshot as its baseline.
export default function StocktakeScreen() {
  const {
    activeItems, openStocktake, openItemsById, history,
    loading, refresh,
    startStocktake, upsertStocktakeItem, completeStocktake, discardStocktake,
  } = useStocktakeData()

  const [search, setSearch]       = useState('')
  const [drafts, setDrafts]       = useState({})    // { item_id: 'string user typed' }
  const [savingId, setSavingId]   = useState(null)
  const [completing, setCompleting] = useState(false)
  const [toast, setToast]         = useState('')
  const [starting, setStarting]   = useState(false)

  // Hydrate the drafts dict once we have the open stocktake's items
  // — start with whatever was saved last so partial counts persist.
  useEffect(() => {
    if (!openStocktake) {
      setDrafts({})
      return
    }
    const next = {}
    for (const [itemId, row] of openItemsById) {
      next[itemId] = String(row.counted_qty ?? '')
    }
    setDrafts(next)
  }, [openStocktake?.id, openItemsById.size]) // eslint-disable-line react-hooks/exhaustive-deps

  // Filter the items by search query. Search matches name + category.
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return activeItems
    return activeItems.filter((i) =>
      (i.name || '').toLowerCase().includes(q) ||
      (i.category || '').toLowerCase().includes(q)
    )
  }, [activeItems, search])

  // Running totals across counted-so-far items in the open stocktake
  // — gives the manager a live "you're at $X variance, $Y items
  // remaining" without waiting until they save.
  const liveTotals = useMemo(() => {
    if (!openStocktake) {
      return { counted: 0, total: 0, variance_cost: 0, variance_qty: 0 }
    }
    let counted = 0, variance_qty = 0, variance_cost = 0
    for (const item of activeItems) {
      const row = openItemsById.get(item.id)
      const draft = drafts[item.id]
      const draftN = draft != null && draft !== '' ? Number(draft) : null
      const countedN = draftN != null && Number.isFinite(draftN)
        ? draftN
        : (row ? Number(row.counted_qty) : null)
      if (countedN == null) continue
      counted++
      const expected = row ? Number(row.expected_qty) : Number(item.current_on_hand || 0)
      const dQty = countedN - expected
      const dCost = dQty * Number(item.cost_per_unit || 0)
      variance_qty  += dQty
      variance_cost += dCost
    }
    return { counted, total: activeItems.length, variance_qty, variance_cost }
  }, [openStocktake, activeItems, openItemsById, drafts])

  if (loading && !openStocktake && activeItems.length === 0) {
    return (
      <div className="min-h-full bg-white pb-12">
        <main className="mx-auto max-w-screen-sm">
          <SkeletonCard />
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={5} className="mt-2" />
          </section>
        </main>
      </div>
    )
  }

  // ---------- Handlers --------------------------------------

  async function handleStart() {
    if (starting) return
    setStarting(true)
    try {
      await startStocktake()
      setToast('Stocktake started — walk the kitchen and tap each item.')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleStart]', e)
      setToast(`Could not start: ${e?.message || 'try again.'}`)
    } finally {
      setStarting(false)
    }
  }

  async function handleSaveItem(item) {
    if (!openStocktake) return
    if (savingId) return
    const draft = drafts[item.id]
    if (draft == null || draft === '') {
      setToast('Type a count first.')
      return
    }
    const counted = Number(draft)
    if (!Number.isFinite(counted) || counted < 0) {
      setToast('Counts must be a non-negative number.')
      return
    }
    setSavingId(item.id)
    try {
      await upsertStocktakeItem({
        stocktake_id: openStocktake.id,
        item_id:      item.id,
        counted_qty:  counted,
        expected_qty: openItemsById.get(item.id)?.expected_qty
                      ?? Number(item.current_on_hand || 0),
      })
      // Don't refresh on every keystroke — refresh on demand or
      // when the manager hits Save & complete. But pull the row
      // metadata so liveTotals updates.
      await refresh()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleSaveItem]', e)
      setToast(`Could not save count: ${e?.message || 'try again.'}`)
    } finally {
      setSavingId(null)
    }
  }

  async function handleComplete() {
    if (!openStocktake || completing) return
    if (liveTotals.counted === 0) {
      setToast('Type at least one count before completing.')
      return
    }
    const remaining = liveTotals.total - liveTotals.counted
    const ok = window.confirm(
      `Complete this stocktake?\n\n` +
      `${liveTotals.counted} of ${liveTotals.total} items counted` +
      (remaining > 0 ? ` (${remaining} skipped — they'll keep their current on-hand)` : '') +
      `.\n\nThis updates each counted item's on-hand to your number, snapshots ` +
      `the totals as the new variance baseline, and closes this stocktake.\n\nContinue?`
    )
    if (!ok) return
    // First, make sure any unsaved drafts are persisted.
    for (const item of activeItems) {
      const draft = drafts[item.id]
      const existing = openItemsById.get(item.id)
      const draftN = draft != null && draft !== '' ? Number(draft) : null
      if (draftN != null && Number.isFinite(draftN) && draftN >= 0) {
        const sameAsExisting = existing && Number(existing.counted_qty) === draftN
        if (!sameAsExisting) {
          await upsertStocktakeItem({
            stocktake_id: openStocktake.id,
            item_id:      item.id,
            counted_qty:  draftN,
            expected_qty: existing?.expected_qty
                          ?? Number(item.current_on_hand || 0),
          })
        }
      }
    }
    setCompleting(true)
    try {
      const summary = await completeStocktake(openStocktake.id)
      const sign = summary.total_variance_qty > 0 ? '+' : ''
      setToast(
        `Stocktake complete — ${summary.item_count} ${summary.item_count === 1 ? 'item' : 'items'} ` +
        `counted, net variance ${sign}${summary.total_variance_qty.toFixed(2)} units. ` +
        `Variance baseline updated.`
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleComplete]', e)
      setToast(`Could not complete: ${e?.message || 'try again.'}`)
    } finally {
      setCompleting(false)
    }
  }

  async function handleDiscard() {
    if (!openStocktake) return
    const ok = window.confirm(
      'Discard this stocktake?\n\n' +
      'All in-progress counts will be deleted. Inventory on-hand values ' +
      'are NOT affected. Continue?'
    )
    if (!ok) return
    try {
      await discardStocktake(openStocktake.id)
      setToast('Stocktake discarded.')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleDiscard]', e)
      setToast(`Could not discard: ${e?.message || 'try again.'}`)
    }
  }

  // ---------- Idle (no open stocktake) ----------------------
  if (!openStocktake) {
    return (
      <div className="min-h-full bg-white pb-12">
        <main className="mx-auto max-w-screen-sm">
          <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
            <div className="text-xs font-medium text-black/55">
              Stocktake
            </div>
            <p className="mt-1 text-sm text-black/65 leading-snug">
              Walk the kitchen and count what's actually on hand.
              Saving completes the count, updates each item's on-hand value,
              and snapshots the totals as the next variance baseline.
            </p>
            <button
              type="button"
              onClick={handleStart}
              disabled={starting || activeItems.length === 0}
              className="mt-4 min-h-btn w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {starting ? 'Starting…' : `+ Start new count (${activeItems.length} items)`}
            </button>
            {activeItems.length === 0 && (
              <p className="mt-2 text-xs text-black/55 leading-snug">
                No items to count yet. Add items in Settings → Items & Pars
                (or import your POS) and they'll show up here to count.
              </p>
            )}
          </section>

          {history.length > 0 && (
            <section className="mx-4 mt-6" aria-label="Stocktake history">
              <div className="mb-2 text-base font-medium">Past counts</div>
              <ul className="overflow-hidden rounded-xl border border-black/10">
                {history.map((s) => {
                  const items = s.items ?? []
                  const itemCount = items.length
                  const totalVarQty = items.reduce(
                    (acc, x) => acc + (Number(x.counted_qty) - Number(x.expected_qty)),
                    0,
                  )
                  return (
                    <li
                      key={s.id}
                      className="flex items-start justify-between gap-3 border-b border-black/5 px-4 py-3 text-sm last:border-b-0"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium tabular-nums">
                          {new Date(s.completed_at).toLocaleString()}
                        </div>
                        <div className="mt-0.5 text-xs text-black/55">
                          {itemCount} {itemCount === 1 ? 'item' : 'items'} counted
                          {s.notes ? ` · ${s.notes}` : ''}
                        </div>
                      </div>
                      <div className="shrink-0 text-right text-xs tabular-nums">
                        <span className={`font-medium ${
                          totalVarQty > 0 ? 'text-emerald-700'
                          : totalVarQty < 0 ? 'text-red-700'
                          : 'text-black/60'
                        }`}>
                          {totalVarQty > 0 ? '+' : ''}{totalVarQty.toFixed(2)} net
                        </span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}

          <Toast message={toast} onDone={() => setToast('')} />
        </main>
      </div>
    )
  }

  // ---------- Active (counting in progress) ------------------
  return (
    <div className="min-h-full bg-white pb-32">
      <main className="mx-auto max-w-screen-sm">
        {/* Header card */}
        <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
          <div className="flex items-baseline justify-between gap-2">
            <div>
              <div className="text-xs font-medium text-black/55">
                Stocktake in progress
              </div>
              <div className="text-xs text-black/55 tabular-nums">
                Started {new Date(openStocktake.started_at).toLocaleString()}
              </div>
            </div>
            <button
              type="button"
              onClick={handleDiscard}
              className="rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              Discard
            </button>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2">
            <SummaryTile
              label="Counted"
              value={`${liveTotals.counted} / ${liveTotals.total}`}
              tone="default"
            />
            <SummaryTile
              label="Net variance qty"
              value={`${liveTotals.variance_qty > 0 ? '+' : ''}${liveTotals.variance_qty.toFixed(2)}`}
              tone={Math.abs(liveTotals.variance_qty) < 0.001 ? 'green'
                    : liveTotals.variance_qty > 0 ? 'green' : 'red'}
            />
            <SummaryTile
              label="Net variance $"
              value={`${liveTotals.variance_cost >= 0 ? '+' : ''}${formatMoney(liveTotals.variance_cost)}`}
              tone={Math.abs(liveTotals.variance_cost) < 0.01 ? 'green'
                    : liveTotals.variance_cost > 0 ? 'green' : 'red'}
            />
          </div>
        </section>

        {/* Search */}
        <div className="mx-4 mt-4">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items by name or category…"
            className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
          />
        </div>

        {/* Items list */}
        <section className="mx-4 mt-4" aria-label="Items to count">
          <ul className="overflow-hidden rounded-xl border border-black/10">
            {visibleItems.map((item) => {
              const existing = openItemsById.get(item.id)
              const expected = existing
                ? Number(existing.expected_qty)
                : Number(item.current_on_hand || 0)
              const draft = drafts[item.id] ?? ''
              const draftN = draft !== '' ? Number(draft) : null
              const isSaving = savingId === item.id
              const counted  = !!existing
              const varianceQty = draftN != null && Number.isFinite(draftN)
                ? draftN - expected
                : (existing ? Number(existing.counted_qty) - expected : null)
              const varianceCost = varianceQty != null
                ? varianceQty * Number(item.cost_per_unit || 0)
                : 0
              return (
                <li
                  key={item.id}
                  className="border-b border-black/5 px-4 py-3 last:border-b-0"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-medium">
                        {item.name}
                        {counted && (
                          <span className="ml-2 inline-block rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                            counted
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-black/55">
                        Expected {formatQty(expected, item.unit)}
                        {item.category ? ` · ${item.category}` : ''}
                        {Number(item.pack_size) > 1 && item.pack_unit && (
                          <> · {item.pack_size} {item.pack_unit}/{item.unit}</>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 grid grid-cols-[1fr_auto_auto] items-center gap-2">
                    <input
                      type="number"
                      inputMode="decimal"
                      step="any"
                      min="0"
                      value={draft}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [item.id]: e.target.value }))
                      }
                      placeholder={`count (${item.unit})`}
                      className="w-full rounded-lg border border-black/15 px-3 py-2 text-base font-medium tabular-nums outline-none focus:border-blue-600"
                    />
                    <button
                      type="button"
                      onClick={() => handleSaveItem(item)}
                      disabled={isSaving || draft === ''}
                      className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSaving ? '…' : (counted ? 'Update' : 'Save')}
                    </button>
                    {varianceQty != null && draft !== '' && (
                      <div className={`shrink-0 text-right text-xs tabular-nums ${
                        Math.abs(varianceQty) < 0.001 ? 'text-emerald-700'
                        : varianceQty > 0 ? 'text-emerald-700'
                        : 'text-red-700'
                      }`}>
                        <div className="font-medium">
                          {varianceQty > 0 ? '+' : ''}
                          {varianceQty.toFixed(2)}
                        </div>
                        <div className="opacity-75">
                          {varianceCost >= 0 ? '+' : ''}
                          {formatMoney(varianceCost)}
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </section>

        {/* Sticky footer CTA */}
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-black/10 bg-white/95 px-4 py-3 backdrop-blur safe-bottom">
          <div className="mx-auto max-w-screen-sm">
            <button
              type="button"
              onClick={handleComplete}
              disabled={completing || liveTotals.counted === 0}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {completing
                ? 'Completing…'
                : liveTotals.counted === 0
                  ? 'Count at least one item to complete'
                  : `Save & complete · ${liveTotals.counted} ${liveTotals.counted === 1 ? 'item' : 'items'}`}
            </button>
          </div>
        </div>

        <Toast message={toast} onDone={() => setToast('')} />
      </main>
    </div>
  )
}

function SummaryTile({ label, value, tone = 'default' }) {
  const palette = tone === 'green'
    ? 'border-emerald-200 bg-emerald-50/40 text-emerald-800'
    : tone === 'red'
      ? 'border-red-200 bg-red-50 text-red-800'
      : 'border-black/10 bg-black/[0.02] text-black/85'
  return (
    <div className={`rounded-xl border p-2 ${palette}`}>
      <div className="text-[10px] font-medium opacity-75">
        {label}
      </div>
      <div className="mt-0.5 text-base font-medium tabular-nums">
        {value}
      </div>
    </div>
  )
}
